<script lang="ts" setup>
import type {
  GetOrganizationMembersQuery,
  GetOrganizationMembersQueryVariables,
} from '@/components/UsersSelector.generated'
import { GetOrganizationMembersDocument } from '@/components/UsersSelector.generated'
import { SetTeamAdminMutationDocument } from './TeamModalMembers.generated'

interface MemberInfo {
  id: string
  display: string
  email: string
  gravatar: string
}

const props = defineProps<{
  teamId: string | undefined
  isGithubSynced: boolean
}>()

const memberEdits = ref<EditSet>(newEditSet())
const adminEdits = ref<EditSet>(newEditSet())
const usersCache = ref<Map<string, MemberInfo>>(new Map())

const {
  members: serverMembers,
  adminMembers: loadedAdmins,
  isFetching: isLoading,
  fetchAll,
  reset: resetLoaded,
} = useTeamMembers()

const isServerAdmin = (id: string) => loadedAdmins.value.some((a) => a.id === id)

const currentMembers = computed<MemberInfo[]>(() => {
  const serverIds = new Set(serverMembers.value.map((m) => m.id))
  const result = serverMembers.value.filter((m) => !memberEdits.value.removed.has(m.id))
  for (const id of memberEdits.value.added) {
    if (serverIds.has(id)) continue
    const cached = usersCache.value.get(id)
    if (cached) result.push(cached)
  }
  return result
})

const currentMemberIds = computed<Set<string>>(() => new Set(currentMembers.value.map((m) => m.id)))

const currentAdminIds = computed<Set<string>>(() => {
  const result = new Set<string>()
  for (const admin of loadedAdmins.value) {
    if (!adminEdits.value.removed.has(admin.id)) result.add(admin.id)
  }
  for (const id of adminEdits.value.added) result.add(id)
  for (const id of Array.from(result)) {
    if (!currentMemberIds.value.has(id)) result.delete(id)
  }
  return result
})

const { isFetching: isFetchingMembers, search: searchMembers } = useRelayField<
  GetOrganizationMembersQuery,
  GetOrganizationMembersQueryVariables,
  SelectMenuOption[]
>(
  GetOrganizationMembersDocument,
  computed(() => ({ first: 50, term: '', existing: [] })),
  ({ data }) => {
    const nodes = getListOfNodes(data?.organization.users)
    for (const node of nodes) {
      usersCache.value.set(node.id, {
        id: node.id,
        display: node.display,
        email: node.email,
        gravatar: node.gravatar,
      })
    }
    return nodes
      .filter((node) => !currentMemberIds.value.has(node.id))
      .map((node) => ({
        label: `${node.display}${node.display === node.email || !node.email ? '' : ` (${node.email})`}`,
        value: node.id,
      }))
  },
)

const { execute: setTeamAdmin } = useMutation(SetTeamAdminMutationDocument, {
  refetchTags: ['team-members'],
})

function addMember(userId: string) {
  if (!userId || currentMemberIds.value.has(userId)) return
  if (memberEdits.value.removed.has(userId)) {
    memberEdits.value.removed.delete(userId)
  } else {
    memberEdits.value.added.add(userId)
  }
}

function removeMember(userId: string) {
  if (memberEdits.value.added.has(userId)) {
    memberEdits.value.added.delete(userId)
  } else {
    memberEdits.value.removed.add(userId)
  }
}

function toggleAdmin(userId: string) {
  const isCurrentlyAdmin = currentAdminIds.value.has(userId)
  if (isCurrentlyAdmin) {
    if (adminEdits.value.added.has(userId)) {
      adminEdits.value.added.delete(userId)
    } else {
      adminEdits.value.removed.add(userId)
    }
  } else {
    if (adminEdits.value.removed.has(userId)) {
      adminEdits.value.removed.delete(userId)
    } else {
      adminEdits.value.added.add(userId)
    }
  }
}

function reset() {
  memberEdits.value = newEditSet()
  adminEdits.value = newEditSet()
  resetLoaded()
}

async function load(teamId: string): Promise<void> {
  await fetchAll(teamId)
}

function getFinalMemberIds(): string[] {
  return currentMembers.value.map((m) => m.id)
}

async function applyAdminChanges(teamId: string): Promise<boolean> {
  // We can only promote users who will actually be members after this save.
  const toPromote = Array.from(adminEdits.value.added).filter(
    (id) => !isServerAdmin(id) && currentMemberIds.value.has(id),
  )
  const toDemote = Array.from(adminEdits.value.removed).filter(isServerAdmin)

  for (const userId of toPromote) {
    const response = await setTeamAdmin({ input: { teamId, userId, isAdmin: true } })
    const error = handleMutationError({ errors: response.data?.setTeamAdmin?.errors, combinedError: response.error })
    if (error) return false
  }
  for (const userId of toDemote) {
    const response = await setTeamAdmin({ input: { teamId, userId, isAdmin: false } })
    const error = handleMutationError({ errors: response.data?.setTeamAdmin?.errors, combinedError: response.error })
    if (error) return false
  }
  return true
}

defineExpose({
  getFinalMemberIds,
  applyAdminChanges,
  reset,
  load,
  isLoading,
})
</script>

<template>
  <div class="flex min-h-0 flex-1 flex-col">
    <div class="mb-4 text-lg font-semibold">Members</div>

    <div v-if="currentMembers.length > 0" class="min-h-0 flex-1 overflow-y-auto">
      <div v-for="member in currentMembers" :key="member.id" class="flex items-center justify-between py-2 pr-3">
        <div class="flex items-center gap-2.5">
          <UAvatar :src="member.gravatar" :alt="member.display" size="xs" />
          <div class="min-w-0">
            <div class="truncate text-sm font-medium text-gray-900 dark:text-white">{{ member.display }}</div>
            <div v-if="member.email && member.email !== member.display" class="truncate text-xs text-gray-500">
              {{ member.email }}
            </div>
          </div>
        </div>
        <div class="flex items-center gap-1">
          <label class="flex cursor-pointer items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
            <UToggle
              size="sm"
              on-icon="light:crown"
              :model-value="currentAdminIds.has(member.id)"
              @update:model-value="toggleAdmin(member.id)"
            />
            Team admin
          </label>
          <UTooltip
            :text="SYNC_DISABLED_TOOLTIP"
            :prevent="!isGithubSynced"
            :ui="{ base: 'h-auto !whitespace-normal max-w-xs' }"
          >
            <UButton
              icon="light:xmark"
              variant="ghost"
              color="gray"
              size="xs"
              :disabled="isGithubSynced"
              @click="removeMember(member.id)"
            />
          </UTooltip>
        </div>
      </div>
    </div>
    <div v-else class="flex-1 text-sm text-gray-500">No members added yet.</div>

    <UTooltip
      :text="SYNC_DISABLED_TOOLTIP"
      :prevent="!isGithubSynced"
      class="mt-4 block"
      :ui="{ base: 'h-auto !whitespace-normal max-w-xs' }"
    >
      <SelectMenu
        :model-value="null"
        :is-fetching="isFetchingMembers"
        :searchable="searchMembers"
        :disabled="isGithubSynced"
        placeholder="Add a member..."
        leading-icon="light:magnifying-glass"
        option-attribute="label"
        value-attribute="value"
        size="md"
        @update:model-value="addMember"
      />
    </UTooltip>
  </div>
</template>
